import { z } from 'zod';
/**
 * Shared enums. Values match the Prisma/Postgres enums in
 * `docs/02-DATA-MODEL.md` EXACTLY — the API and the PWA both switch on these
 * strings, so a drift here is a silent contract break.
 */
// ---- 02-DATA-MODEL.md §3 Identity and access ----------------------------
/**
 * `users.role` — one system-wide role per person (Phase 4, the rewritten
 * D-02). A worker is a worker everywhere; a supervisor supervises every
 * contract they are scoped to; `platform_admin` is a role value, not a flag
 * (`users.is_platform_admin` no longer exists). A membership carries no role.
 *
 * This is the READ side. `team_lead` is reserved (D-02): the database enum
 * carries it, no permissions are seeded for it and no write path accepts it,
 * so it is tolerated here and refused by `AssignableRoleSchema` below.
 */
export const RoleSchema = z.enum(['worker', 'team_lead', 'supervisor', 'admin', 'platform_admin']);
/**
 * The WRITE-path subset of `RoleSchema`: every role a caller may give a user
 * (`POST /users`, `POST /users/:id/role`) or filter a list by. `team_lead` is
 * excluded on purpose; `platform_admin` is included because it is a role —
 * *who* may grant it is the API's rank rule (`ROLE_RANK`), not the schema's.
 *
 * Not to be confused with `isAssignableRole()` below, which answers a
 * different question: whether a role can be assigned *to a shift*.
 */
export const AssignableRoleSchema = z.enum(['worker', 'supervisor', 'admin', 'platform_admin']);
/**
 * The rank ladder `worker < supervisor < admin < platform_admin`, defined
 * once and used for exactly one rule: a caller may not grant a role above
 * their own (`ROLE_ABOVE_CALLER`). It is **not** a permission-inheritance
 * mechanism — `role_permissions` stays the only source of permissions.
 * `team_lead` never reaches it because no write path accepts it.
 */
export const ROLE_RANK = {
    worker: 0,
    supervisor: 1,
    admin: 2,
    platform_admin: 3,
};
/**
 * The ladder extended for the one value it does not carry: the reserved
 * `team_lead` can never be granted, so it ranks below everything — a
 * `team_lead` caller is outranked by every requested role, and a `team_lead`
 * target never outranks a caller. This is the one comparison behind
 * `ROLE_ABOVE_CALLER`, on both the requested role and the target's current one.
 */
export const rankOf = (role) => role === 'team_lead' ? Number.NEGATIVE_INFINITY : ROLE_RANK[role];
/**
 * "Can a person holding this role be scheduled on a shift?" — the one
 * definition the API and both apps import. Only a worker fills a slot;
 * supervisors and admins run the shift, they are not assigned to it
 * (widening this is a separate product call).
 */
export const isAssignableRole = (role) => role === 'worker';
/**
 * The roles whose work is the *portal*, not the floor — the people who sign
 * into the admin app rather than punch on a phone.
 */
const PORTAL_ROLES = ['supervisor', 'admin', 'platform_admin'];
/** "Does this role belong in the admin app?" — the one definition both apps import. */
export const isPortalRole = (role) => PORTAL_ROLES.includes(role);
/**
 * "Is this person's account tied to one device?" (D-06, amended 2026-08-30).
 *
 * Device binding is a **punch** control: it is what makes "E1007 clocked in at
 * 22:04" mean *the person holding E1007's phone*, and it is the reason a
 * shared phone cannot quietly punch a whole crew in. None of that applies to a
 * supervisor or an admin. They never punch (`isAssignableRole`), and over one
 * night they legitimately sign in from the trailer laptop, the office desktop
 * and a borrowed tablet — so binding them buys no payroll integrity and costs
 * a lockout every time, with a `device:reset` needed to undo it.
 *
 * Written as the negation of `isPortalRole` on purpose: a role nobody has
 * classified yet **binds**. The reserved `team_lead` therefore fails closed if
 * it is ever granted, which is the safe direction for a control that protects
 * payroll.
 */
export const isDeviceBoundRole = (role) => !isPortalRole(role);
/** `users.status`. */
export const UserStatusSchema = z.enum(['active', 'inactive', 'suspended']);
/** `users.locale`. */
export const LocaleSchema = z.enum(['fr', 'en']);
/** `devices.status`. Not directly in §9's module list, but needed by the device-reset response. */
export const DeviceStatusSchema = z.enum(['active', 'revoked']);
// ---- 02-DATA-MODEL.md §4 Operational structure ---------------------------
/** `contracts.status`. */
export const ContractStatusSchema = z.enum(['draft', 'active', 'suspended', 'ended']);
/** `sites.status`. */
export const SiteStatusSchema = z.enum(['active', 'inactive']);
/** `zones.status`. */
export const ZoneStatusSchema = z.enum(['active', 'inactive']);
// ---- 02-DATA-MODEL.md §5 Scheduling ---------------------------------------
/** `shift_series.status`. */
export const SeriesStatusSchema = z.enum(['active', 'paused', 'ended']);
/** `shifts.status`. */
export const ShiftStatusSchema = z.enum([
    'scheduled',
    'open',
    'in_progress',
    'completed',
    'cancelled',
    'no_show',
]);
// ---- 02-DATA-MODEL.md §6 Attendance ---------------------------------------
/** `punches.type`. */
export const PunchTypeSchema = z.enum(['IN', 'OUT']);
/** `punches.source`. */
export const PunchSourceSchema = z.enum(['online', 'offline_synced', 'supervisor_created']);
/** `punches.gps_status`. */
export const GpsStatusSchema = z.enum(['ok', 'denied', 'unavailable', 'timeout']);
/** `punches.geo_result`. */
export const GeoResultSchema = z.enum(['inside', 'outside', 'unverifiable', 'not_captured']);
/** `punch_adjustments.kind`. */
export const AdjustmentKindSchema = z.enum(['create_missing', 'amend_time', 'close_open', 'void']);
// ---- 02-DATA-MODEL.md §7 Exceptions ---------------------------------------
/** `exceptions.type` — the exact catalogue from §7. */
export const ExceptionTypeSchema = z.enum([
    'STALE_OPEN_PUNCH',
    'LATE_PUNCH_OUT',
    'NO_SHOW',
    'GEOFENCE_OUTSIDE',
    'GEOFENCE_UNVERIFIABLE',
    'GPS_DENIED',
    'CLOCK_DRIFT',
    'OFFLINE_SYNC_DELAYED',
    'UNKNOWN_DEVICE',
    // Phase 5 (tasks, 2026-09-02). Default severities live in the API's
    // `DEFAULT_EXCEPTION_SEVERITY` catalogue, as for every other type:
    // PUNCH_OUT_TASKS_BYPASSED high, TASKS_NOT_COMPLETED / TASK_NOT_COMPLETED
    // normal.
    'PUNCH_OUT_TASKS_BYPASSED',
    'TASKS_NOT_COMPLETED',
    'TASK_NOT_COMPLETED',
]);
/** `exceptions.severity`. */
export const ExceptionSeveritySchema = z.enum(['low', 'normal', 'high']);
/** `exceptions.status`. */
export const ExceptionStatusSchema = z.enum(['open', 'acknowledged', 'resolved', 'dismissed']);
// ---- 02-DATA-MODEL.md §8 Time and payroll ---------------------------------
/** Classification of a computed hours entry (live, ad-hoc, per date range). */
export const TimesheetSourceSchema = z.enum(['computed', 'adjusted', 'supervisor_created']);
// ---- 02-DATA-MODEL.md §2 Configuration (platform_settings) ----------------
/** `platform_settings.gps_policy` / effective per-contract policy. */
export const GpsPolicySchema = z.enum(['off', 'warn', 'block']);
/** `platform_settings.device_policy`. */
export const DevicePolicySchema = z.enum(['off', 'warn', 'strict']);
//# sourceMappingURL=enums.js.map